import { Link } from "react-router-dom";
import ServiceDetailLayout from "@/components/site/ServiceDetailLayout";

export default function Marketo() {
  return (
    <>
      <ServiceDetailLayout
        eyebrow="Adobe Marketo Engage"
        title="Adobe Marketo Engage, fixed by people who run it."
        subtitle="If your Marketo instance is drifting — broken Salesforce sync, lifecycle stages nobody trusts, scoring that hasn't been calibrated in two years, campaigns slipping at launch — this is the work we do every day. Senior operators on the platform, not slide decks. Includes Adobe's newest releases: the new Email Designer (Adobe IMS migration) and the Marketo MCP Server."
        cta="Book a Marketo Review"
        seo={{
          title: "Marketo Services | Adobe Marketo Engage Consultant | Xadentra",
          description:
            "Expert Adobe Marketo Engage services for campaign builds, nurture programs, lead scoring, CRM sync, reporting, and marketing operations support.",
          path: "/marketo-services",
        }}
        sections={[
          {
            heading: "Marketo campaign operations",
            items: [
              "Day-to-day campaign operations support — request intake, build, QA, launch, post-launch fixes",
              "Smart campaigns and automation logic for triggered, batch, and operational programs",
              "Tokens, modular templates, and program cloning patterns that keep campaign velocity high",
              "Operational hygiene programs that keep the instance healthy in the background",
              "Change-management governance so launches are documented, reviewed, and traceable",
            ],
          },
          {
            heading: "Email program builds",
            items: [
              "Modular email template system aligned with your brand and rendering across major clients",
              "Newsletter, promotional, transactional, and lifecycle email program setup",
              "Dynamic content and personalization driven by Marketo person fields and behavioural data",
              "A/B testing structure, deliverability hygiene, and send-time governance",
              "New Email Designer migration — IMS readiness, phased rollout, modular fragments, parity testing",
            ],
          },
          {
            heading: "Landing page and form setup",
            items: [
              "Branded, conversion-focused landing page templates with reusable components",
              "Form builds with progressive profiling, conditional fields, and consent capture",
              "Thank-you flows, redirect logic, and downstream automation tied to form fills",
              "Mobile-responsive layouts that pass Core Web Vitals on real B2B traffic",
              "Tracking integration so every form fill captures UTMs and feeds reporting cleanly",
            ],
          },
          {
            heading: "Nurture program setup",
            items: [
              "Nurture program design from awareness through buying-stage with branching content paths",
              "Engagement programs, drip programs, and stream-based architecture as the use case demands",
              "Content mapping aligned with funnel stage and persona",
              "Re-engagement and recycle paths so cold leads don't disappear",
              "Cadence and frequency-capping governance to protect your sender reputation",
            ],
          },
          {
            heading: "Lead scoring",
            items: [
              "Behavioural + fit-based scoring tied to sales-acceptance data",
              "Decay logic, threshold calibration, and sales-feedback loops",
              "Score reset and recycle rules so MQLs stay clean",
              "Predictive scoring integration where it adds value over rules-based",
              "Score-driven routing into Salesforce with full transparency",
            ],
          },
          {
            heading: "Lead lifecycle management",
            items: [
              "Lifecycle and revenue cycle model design — stage definitions, transitions, SLAs",
              "Recycle paths, disqualification rules, and re-engagement triggers",
              "Stage-progression reporting so marketing and sales agree on what's working",
              "Alert programs and internal notifications for stuck leads",
              "Lifecycle audits — diagnostics that surface where pipeline is leaking",
            ],
          },
          {
            heading: "Salesforce CRM sync",
            items: [
              "Salesforce campaign sync hardening — field mapping, picklist drift, error monitoring",
              "Sync-error triage and resolution playbooks",
              "Lead-to-contact conversion handling, lead routing, and ownership logic",
              "Custom-object sync where the use case requires it",
              "Sync health dashboards and alerts so issues surface before campaigns break",
            ],
          },
          {
            heading: "Data quality and segmentation",
            items: [
              "Database hygiene, dedupe, and field standardization",
              "Reusable smart-list libraries for segmentation",
              "Consent and preference-centre management aligned with GDPR/CASL/CAN-SPAM",
              "Data-enrichment integrations (ZoomInfo, Clearbit, Cognism) where useful",
              "Quarterly data-quality audits with measurable cleanup outcomes",
            ],
          },
          {
            heading: "Reporting and attribution",
            items: [
              "Executive-readable pipeline reporting structures",
              "Campaign performance, program ROI, and funnel velocity dashboards",
              "First-touch / multi-touch attribution model setup",
              "Lifecycle and stage-progression reporting",
              "GA4, Looker, and Tableau integration where it surfaces the story better",
            ],
          },
          {
            heading: "Marketo QA and launch support",
            items: [
              "Pre-launch QA checklists for emails, landing pages, programs, and smart campaigns",
              "Automated parity testing across template variants and editor migrations",
              "Sandbox / workspace strategies for safe rollouts",
              "Rollback playbooks so production launches stay reversible",
              "Post-launch monitoring windows with on-call coverage",
            ],
          },
          {
            heading: "Marketing operations consulting",
            items: [
              "MOPs strategy reviews, instance health audits, and roadmap definition",
              "Adobe Marketo Engage MCP Server enablement — agentic automation, AI-assisted workflows, scoped access, guardrails",
              "Team enablement, documentation, and SOP libraries",
              "Hiring frameworks, ramp plans, and interim MOPs leadership",
              "Fractional senior support — embed a senior operator in your team for ongoing capacity",
            ],
          },
        ]}
        related={[
          { to: "/services/salesforce-crm", label: "Salesforce & CRM Operations" },
          { to: "/services/managed-services", label: "Managed Services" },
          { to: "/services/reporting-attribution", label: "Reporting & Attribution" },
        ]}
      />

      {/* Internal links block — explicit, crawlable anchors back to the core funnel pages */}
      <section
        aria-label="Related Xadentra pages"
        className="bg-[#0B1A2E] text-white border-t border-white/10"
        data-testid="marketo-internal-links"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">Continue exploring</div>
          <h2 className="font-display mt-4 text-3xl lg:text-4xl font-bold tracking-tight">
            Continue exploring Xadentra
          </h2>
          <p className="mt-4 text-sm lg:text-base text-white/70 max-w-2xl">
            See how our Adobe Marketo Engage services connect to the rest of our B2B
            marketing automation work, the outcomes we've delivered, and how to
            start the conversation.
          </p>
          <ul className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
            <li>
              <Link
                to="/"
                data-testid="marketo-link-home"
                className="block rounded-xl border border-white/10 hover:border-[#6D4DFF]/60 bg-white/[0.04] p-5 transition"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">Home</div>
                <div className="mt-1.5 font-display text-base font-semibold text-white">
                  B2B Marketing Automation & Marketo Services →
                </div>
              </Link>
            </li>
            <li>
              <Link
                to="/case-studies"
                data-testid="marketo-link-case-studies"
                className="block rounded-xl border border-white/10 hover:border-[#6D4DFF]/60 bg-white/[0.04] p-5 transition"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">Case Studies</div>
                <div className="mt-1.5 font-display text-base font-semibold text-white">
                  Adobe Marketo Engage outcomes →
                </div>
              </Link>
            </li>
            <li>
              <Link
                to="/contact"
                data-testid="marketo-link-contact"
                className="block rounded-xl border border-white/10 hover:border-[#6D4DFF]/60 bg-white/[0.04] p-5 transition"
              >
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">Contact</div>
                <div className="mt-1.5 font-display text-base font-semibold text-white">
                  Talk to a Marketo specialist →
                </div>
              </Link>
            </li>
          </ul>
        </div>
      </section>
    </>
  );
}
